import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mykoywob';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
interface QuoteFormProps {
  language: 'en' | 'es';
}
export function QuoteForm({
  language
}: QuoteFormProps) {
  const t = {
    en: {
      eyebrow: 'Free Estimate',
      title: 'GET YOUR FREE QUOTE',
      desc: "Tell us what you need and we'll get back to you with a fast, no-obligation quote.",
      bullets: ['On-time delivery and pickup', 'Regular cleaning and servicing', 'Waste removal', 'Restocking when needed', 'Local service across Odessa, Midland, and West Texas'],
      prefer: 'Prefer to talk to someone?',
      callOrText: 'Call or Text Us',
      available: 'Available 24/7 for emergencies',
      successTitle: 'Quote Request Sent!',
      successDesc: "We'll get back to you within 1 business day with your custom quote.",
      successButton: 'Submit Another Request',
      errorCallUs: 'Please try again or call us at',
      errorFallback: 'Network error. Please try again.',
      labelName: 'Full Name *',
      placeholderName: 'Your full name',
      labelPhone: 'Phone Number *',
      placeholderPhone: '(432) 555-1234',
      labelEmail: 'Email (Optional)',
      placeholderEmail: 'your@email.com',
      labelService: 'Service Needed *',
      placeholderService: 'Select a service...',
      serviceOptions: ['Portable Restroom Units', 'Combo Trailer (Restrooms + Trash)', 'Trash / Dump Trailer', 'HDPE Pipe Fusion Machine', 'Portable Wash Stations', 'Not Sure / Other'],
      labelMessage: 'Additional Details',
      placeholderMessage: 'Event date, location, number of units needed, etc.',
      sending: 'Sending...',
      submit: 'SUBMIT QUOTE REQUEST',
      respond: 'We typically respond within a few hours during business days.'
    },
    es: {
      eyebrow: 'Estimación Gratis',
      title: 'OBTÉN TU COTIZACIÓN GRATIS',
      desc: 'Dinos lo que necesitas y te responderemos con una cotización rápida y sin compromiso.',
      bullets: ['Entrega y recogida a tiempo', 'Limpieza y servicio regular', 'Remoción de residuos', 'Reabastecimiento cuando se necesita', 'Servicio local en Odessa, Midland y West Texas'],
      prefer: '¿Prefieres hablar con alguien?',
      callOrText: 'Llámanos o Escríbenos',
      available: 'Disponible 24/7 para emergencias',
      successTitle: '¡Solicitud Enviada!',
      successDesc: 'Te responderemos dentro de 1 día hábil con tu cotización personalizada.',
      successButton: 'Enviar Otra Solicitud',
      errorCallUs: 'Intenta de nuevo o llámanos al',
      errorFallback: 'Error de red. Por favor intenta de nuevo.',
      labelName: 'Nombre Completo *',
      placeholderName: 'Tu nombre completo',
      labelPhone: 'Número de Teléfono *',
      placeholderPhone: '(432) 555-1234',
      labelEmail: 'Correo Electrónico (Opcional)',
      placeholderEmail: 'tu@correo.com',
      labelService: 'Servicio Necesario *',
      placeholderService: 'Selecciona un servicio...',
      serviceOptions: ['Unidades de Baños Portátiles', 'Remolque Combo (Baños + Basura)', 'Remolque de Basura / Dump Trailer', 'Máquina de Fusión de Tubería HDPE', 'Estaciones de Lavado de Manos', 'Otro / No Estoy Seguro'],
      labelMessage: 'Detalles Adicionales',
      placeholderMessage: 'Fecha del evento, ubicación, número de unidades, etc.',
      sending: 'Enviando...',
      submit: 'ENVIAR SOLICITUD DE COTIZACIÓN',
      respond: 'Normalmente respondemos en pocas horas durante días hábiles.'
    }
  };
  const text = t[language];
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          service: form.service,
          message: form.message,
          _subject: `Quote Request — ${form.service || 'General Inquiry'}`
        })
      });
      if (response.ok) {
        setStatus('success');
        setForm({
          name: '',
          phone: '',
          email: '',
          service: '',
          message: ''
        });
      } else {
        const data = await response.json();
        setErrorMessage(data?.errors?.[0]?.message || 'Something went wrong.');
        setStatus('error');
      }
    } catch {
      setErrorMessage(text.errorFallback);
      setStatus('error');
    }
  };
  return <section id="quote" className="py-12 md:py-20 bg-darkBg border-t border-steel w-full overflow-hidden" data-id="element-250">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-251">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-252">
          {/* Left - Info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left" data-id="element-253">
            <span className="font-montserrat text-gold text-sm font-bold tracking-widest uppercase mb-4 block" data-id="element-254">
              {text.eyebrow}
            </span>
            <h2 className="font-oswald text-4xl sm:text-5xl font-bold text-warmWhite uppercase mb-6" data-id="element-255">
              {text.title}
            </h2>
            <p className="font-inter text-gray-300 text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0" data-id="element-256">
              {text.desc}
            </p>

            <div className="space-y-5 mb-8" data-id="element-257">
              {text.bullets.map((item, i) => <div key={i} className="flex items-center gap-3 justify-center lg:justify-start" data-id="element-258">
                  <CheckCircle2 size={20} fill="#C9952A" className="text-darkBg flex-shrink-0" data-id="element-259" />
                  <span className="font-inter text-warmWhite font-medium" data-id="element-260">
                    {item}
                  </span>
                </div>)}
            </div>

            <div className="bg-carbon p-6 rounded-lg border border-steel text-center lg:text-left" data-id="element-261">
              <p className="font-inter text-gray-400 text-sm mb-2" data-id="element-262">
                {text.prefer}
              </p>
              <p className="font-montserrat text-warmWhite text-xs font-bold tracking-widest uppercase mb-3" data-id="element-263">
                {text.callOrText}
              </p>
              <div className="flex flex-col gap-3" data-id="element-264">
                <div className="flex items-center justify-center lg:justify-start gap-3" data-id="element-265">
                  <span className="font-oswald text-xl text-gold font-bold" data-id="element-266">
                    432-528-7892
                  </span>
                  <a href="tel:+14325287892" className="bg-gold/20 text-gold px-2.5 py-1 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-267">
                    Call
                  </a>
                  <a href="sms:+14325287892" className="bg-gold/20 text-gold px-2.5 py-1 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-268">
                    Text
                  </a>
                </div>
                <div className="flex items-center justify-center lg:justify-start gap-3" data-id="element-269">
                  <span className="font-oswald text-xl text-gold font-bold" data-id="element-270">
                    432-803-4737
                  </span>
                  <a href="tel:+14328034737" className="bg-gold/20 text-gold px-2.5 py-1 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-271">
                    Call
                  </a>
                  <a href="sms:+14328034737" className="bg-gold/20 text-gold px-2.5 py-1 rounded text-xs font-bold hover:bg-gold hover:text-obsidian transition-all" data-id="element-272">
                    Text
                  </a>
                </div>
              </div>
              <p className="font-inter text-gray-400 text-sm mt-3" data-id="element-273">
                {text.available}
              </p>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-274">
            {status === 'success' ? <div className="bg-carbon p-10 rounded-lg border border-gold text-center" data-id="element-275">
                <CheckCircle2 size={64} color="#C9952A" className="mx-auto mb-6" data-id="element-276" />
                <h3 className="font-oswald text-3xl font-bold text-warmWhite mb-4" data-id="element-277">
                  {text.successTitle}
                </h3>
                <p className="font-inter text-gray-300 text-lg mb-6" data-id="element-278">
                  {text.successDesc}
                </p>
                <button onClick={() => setStatus('idle')} className="bg-gold text-obsidian px-8 py-3 rounded font-montserrat font-bold text-sm tracking-wide hover:bg-yellow-500 transition-all duration-300" data-id="element-279">
                  {text.successButton}
                </button>
              </div> : <form onSubmit={handleSubmit} className="bg-carbon p-8 md:p-10 rounded-lg border border-steel space-y-5" data-id="element-280">
                {status === 'error' && <div className="flex items-start gap-3 bg-red-900/30 border border-red-700/50 rounded-lg p-4" data-id="element-281">
                    <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" data-id="element-282" />
                    <div data-id="element-283">
                      <p className="font-inter text-red-300 text-sm font-medium" data-id="element-284">
                        {errorMessage}
                      </p>
                      <p className="font-inter text-red-400/70 text-xs mt-1" data-id="element-285">
                        {text.errorCallUs}{' '}
                        <a href="tel:+14325287892" className="underline hover:text-red-300" data-id="element-286">
                          432-528-7892
                        </a>
                      </p>
                    </div>
                  </div>}

                <div data-id="element-287">
                  <label htmlFor="name" className="block font-montserrat text-xs font-bold tracking-widest uppercase text-gold mb-2" data-id="element-288">
                    {text.labelName}
                  </label>
                  <input type="text" id="name" name="name" required value={form.name} onChange={handleChange} placeholder={text.placeholderName} className="w-full bg-obsidian border border-steel rounded px-4 py-3.5 text-warmWhite font-inter placeholder:text-gray-600 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors" data-id="element-289" />
                </div>

                <div data-id="element-290">
                  <label htmlFor="phone" className="block font-montserrat text-xs font-bold tracking-widest uppercase text-gold mb-2" data-id="element-291">
                    {text.labelPhone}
                  </label>
                  <input type="tel" id="phone" name="phone" required value={form.phone} onChange={handleChange} placeholder={text.placeholderPhone} className="w-full bg-obsidian border border-steel rounded px-4 py-3.5 text-warmWhite font-inter placeholder:text-gray-600 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors" data-id="element-292" />
                </div>

                <div data-id="element-293">
                  <label htmlFor="email" className="block font-montserrat text-xs font-bold tracking-widest uppercase text-gold mb-2" data-id="element-294">
                    {text.labelEmail}
                  </label>
                  <input type="email" id="email" name="email" value={form.email} onChange={handleChange} placeholder={text.placeholderEmail} className="w-full bg-obsidian border border-steel rounded px-4 py-3.5 text-warmWhite font-inter placeholder:text-gray-600 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors" data-id="element-295" />
                </div>

                <div data-id="element-296">
                  <label htmlFor="service" className="block font-montserrat text-xs font-bold tracking-widest uppercase text-gold mb-2" data-id="element-297">
                    {text.labelService}
                  </label>
                  <select id="service" name="service" required value={form.service} onChange={handleChange} className="w-full bg-obsidian border border-steel rounded px-4 py-3.5 text-warmWhite font-inter focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors appearance-none" data-id="element-298">
                    <option value="" disabled data-id="element-299">
                      {text.placeholderService}
                    </option>
                    {text.serviceOptions.map(opt => <option key={opt} value={opt} data-id="element-300">
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-301">
                  <label htmlFor="message" className="block font-montserrat text-xs font-bold tracking-widest uppercase text-gold mb-2" data-id="element-302">
                    {text.labelMessage}
                  </label>
                  <textarea id="message" name="message" rows={4} value={form.message} onChange={handleChange} placeholder={text.placeholderMessage} className="w-full bg-obsidian border border-steel rounded px-4 py-3.5 text-warmWhite font-inter placeholder:text-gray-600 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors resize-none" data-id="element-303" />
                </div>

                <button type="submit" disabled={status === 'submitting'} className="w-full bg-gold text-obsidian px-8 py-4 rounded font-montserrat font-extrabold text-[16px] tracking-wide hover:bg-yellow-500 hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 mt-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-304">
                  {status === 'submitting' ? <>
                      <Loader2 size={18} className="animate-spin" data-id="element-305" />{' '}
                      {text.sending}
                    </> : <>
                      <Send size={18} data-id="element-306" /> {text.submit}
                    </>}
                </button>

                <p className="text-center text-gray-500 text-xs font-inter mt-3" data-id="element-307">
                  {text.respond}
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}